import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  Popover,
  Snackbar,
  Alert,
  Tooltip,
  ClickAwayListener,
  useMediaQuery,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Images } from "../../../utils/Images";
import CurrencySelectorPopup from "../CurrencySelectorPopup";
import { validateDonationAmount } from "../../../utils/donationValidation";
import { fetchCmsHomeAction } from "../../../store/slices/cmsSlice";
import HowToDonateV2 from "../CampaignDetailPageComponents/HowToDonateV2";
import MonthlyUpsellStep from "./MonthlyUpsellStep";
import SubscribeDetailsStep from "./SubscribeDetailsStep";
import { getBeneficiaries } from "../../../api/services/beneficiaryService";

const BRAND_RED = "#F04951";
const MOST_DONATED_RED = "#FF4A60";
const DARK_CHIP = "#3B3A3A";

const currencyList = [
  { code: "INR", symbol: "₹" },
  { code: "USD", symbol: "$" },
  { code: "AED", symbol: "د.إ" },
  { code: "CAD", symbol: "C$" },
  { code: "AUD", symbol: "A$" },
  { code: "GBP", symbol: "£" },
];

const paymentOptions = [
  { name: "PhonePe", src: Images.phonepayIcon },
  { name: "Gpay", src: Images.gpayIcon },
  { name: "Bhim UPI", src: Images.upiIcon },
  { name: "More", src: Images.moreIcon },
];

// ─────────────────────────────────────────────────────────────────────────────
// "What is this?" Popover — overlay rendered via Portal so it sits above ALL
// page elements regardless of any ancestor stacking contexts.
// ─────────────────────────────────────────────────────────────────────────────
const WhatIsThisPopover = ({ anchorEl, onClose, beneficiaries = [] }) => {
  const isOpen = Boolean(anchorEl);
  // Names tooltip: hover on desktop, tap on mobile (phones have no hover). Tapping
  // anywhere else closes it.
  const [namesOpen, setNamesOpen] = useState(false);
  useEffect(() => {
    if (!isOpen) setNamesOpen(false);
  }, [isOpen]);
  // Names shown inline as "<first> +N others" with a tooltip listing them all. One beneficiary
  // can have several campaigns, so names are de-duplicated (case / spacing-insensitive, first
  // spelling kept) — the tooltip and the "+N" count show each person once.
  const seenNames = new Set();
  const beneficiaryNames = beneficiaries
    .map((b) => String(b.beneficiary_name || "").replace(/\s+/g, " ").trim())
    .filter((name) => {
      const key = name.toLowerCase();
      if (!name || seenNames.has(key)) return false;
      seenNames.add(key);
      return true;
    });

  return (
    <>
      {/* Full-page dim overlay — mounted directly on body via Portal */}
      {isOpen &&
        createPortal(
          <Box
            onClick={onClose}
            sx={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              zIndex: 9998,
            }}
          />,
          document.body
        )}

      {/* Popover sits above the overlay */}
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{ zIndex: 9999 }}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0px 8px 32px rgba(0,0,0,0.18)",
            p: "28px",
            width: "380px",
            maxWidth: "calc(100vw - 32px)",
            mt: "8px",
          },
        }}
      >
        <Typography
          sx={{
            fontFamily: "Red Hat Display",
            fontWeight: 700,
            fontSize: "20px",
            color: "#1D1D1B",
            lineHeight: 1.35,
            mb: "16px",
            whiteSpace: "pre-line",
          }}
        >
          {"One Contribution.\nMany Lives Changed Forever"}
        </Typography>
        <Typography
          sx={{
            fontFamily: "Red Hat Display",
            fontWeight: 400,
            fontSize: "14px",
            color: "#4A4A49",
            lineHeight: 1.8,
          }}
        >
          Every penny you donate helps provide life-saving treatment to
          critically ill patients who cannot afford the care they desperately
          need. Your support brings them one step closer to recovery, and a
          chance to live. Don’t wait because the lives of patients like{" "}
          {beneficiaryNames.length > 0 ? (
            <ClickAwayListener onClickAway={() => setNamesOpen(false)}>
            <Tooltip
              title={beneficiaryNames.join(", ")}
              open={namesOpen}
              onOpen={() => setNamesOpen(true)}
              onClose={() => setNamesOpen(false)}
              disableTouchListener
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "rgba(0,0,0,0.85)",
                    backdropFilter: "blur(2px)",
                    color: "#FFFFFF",
                    fontFamily: "Red Hat Display",
                    fontSize: "12px",
                    fontWeight: 500,
                    lineHeight: 1.6,
                    px: "10px",
                    py: "8px",
                    maxWidth: "260px",
                  },
                },
                arrow: { sx: { color: "rgba(0,0,0,0.85)" } },
                // Sit above the popover (zIndex 9999) so the tooltip is in front.
                popper: { sx: { zIndex: 10001 } },
              }}
            >
              <Box
                component="span"
                role="button"
                tabIndex={0}
                onClick={() => setNamesOpen(true)}
                sx={{
                  fontWeight: 700,
                  color: BRAND_RED,
                  cursor: "pointer",
                  textDecoration: "underline",
                  textUnderlineOffset: "2px",
                }}
              >
                {beneficiaryNames[0]}
                {beneficiaryNames.length > 1
                  ? ` +${beneficiaryNames.length - 1} others`
                  : ""}
              </Box>
            </Tooltip>
            </ClickAwayListener>
          ) : (
            "these patients"
          )}{" "}
          depend on your kindness and generosity.
        </Typography>
      </Popover>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main DonationBoxV2 component
// ─────────────────────────────────────────────────────────────────────────────
const DonationBoxV2 = ({
  campaignTitle = "",
  campaignId = null,
  beneficiaryName = "",
  // The campaign row as the public API returns it. Only allow_monthly_donation
  // is read today; both detail pages already passed this before it was used.
  campaignData = null,
  isMonthlyDonation = false,
  onDonationAmountChange,
  initialAmount = 3000,
  // The How To Donate video card is part of this box in the design's desktop
  // sidebar, but it just pads out the mobile bottom sheet — the sheet is opened
  // to donate, not to watch a video. Sheets pass false.
  showHowToDonate = true,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);

  const [donateMode, setDonateMode] = useState("monthly");
  const [baseAmount, setBaseAmount] = useState(initialAmount);
  const [selectedChipIdx, setSelectedChipIdx] = useState(null);
  const [isOtherMode, setIsOtherMode] = useState(false);
  const [currency, setCurrency] = useState(currencyList[0]);
  const [supportPct, setSupportPct] = useState(12);

  // Popover / menu anchors
  const [whatIsThisAnchor, setWhatIsThisAnchor] = useState(null);

  // Beneficiaries of the pooled donate-once program, fetched once when "What is this?" opens
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [beneficiariesFetched, setBeneficiariesFetched] = useState(false);
  const [loadingBeneficiaries, setLoadingBeneficiaries] = useState(false);

  const openWhatIsThis = (e) => {
    setWhatIsThisAnchor(e.currentTarget);
    if (beneficiariesFetched || loadingBeneficiaries) return;
    setLoadingBeneficiaries(true);
    getBeneficiaries()
      .then((res) => {
        const rows = Array.isArray(res?.data) ? res.data : [];
        setBeneficiaries(rows);
        setBeneficiariesFetched(true);
      })
      .catch(() => {
        // Popover copy still works without the list
      })
      .finally(() => setLoadingBeneficiaries(false));
  };
  const [supportMenuAnchor, setSupportMenuAnchor] = useState(null);
  const [currencyMenuAnchor, setCurrencyMenuAnchor] = useState(null);
  const [contributionMenuAnchor, setContributionMenuAnchor] = useState(null);
  const [showCurrencyPopup, setShowCurrencyPopup] = useState(false);
  // Mobile layout (bottom-sheet drawer) — scales the card down per the mobile design.
  const isMobile = useMediaQuery("(max-width:600px)");

  const [selectedContributionIdx, setSelectedContributionIdx] = useState(0);
  // Donate Once: 0 = "<beneficiary> & Others" (pooled program), 1 = "<beneficiary>"
  // (this campaign only). Defaults to the pooled option (index 0).
  const [onceOptionIdx, setOnceOptionIdx] = useState(0);
  const [showUpsell, setShowUpsell] = useState(false);
  // { amount: number, frequency: 'monthly' | 'quarterly' } or null
  const [detailsStep, setDetailsStep] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // campaigns.allow_monthly_donation: 0 means this campaign takes one-time gifts
  // only. Anything else — including a missing field on an older payload or a
  // caller that passes no campaign — counts as allowed, so nothing regresses.
  const monthlyAllowed =
    campaignData?.allow_monthly_donation !== 0 && campaignData?.allow_monthly_donation !== "0";

  // donateMode is read nowhere else, so this one flag switches the whole box.
  const isMonthlyMode = monthlyAllowed && donateMode === "monthly";

  // onceOptions = ["<Name> & Others" (pooled), "<Name>" (this campaign only)].
  // With monthly off the gift must credit this beneficiary, never the pool, so
  // the index is pinned rather than left to the donor.
  const effectiveOnceIdx = monthlyAllowed ? onceOptionIdx : 1;
  // Platform support fee only on "Donate Once"
  const supportAmount = isMonthlyMode ? 0 : Math.round(baseAmount * supportPct / 100);
  const totalAmount = baseAmount + supportAmount;

  useEffect(() => {
    if (!cmsHome) dispatch(fetchCmsHomeAction());
  }, [cmsHome, dispatch]);

  useEffect(() => {
    if (typeof onDonationAmountChange === "function") {
      onDonationAmountChange(totalAmount);
    }
  }, [totalAmount, onDonationAmountChange]);

  // ── CMS preset amounts ────────────────────────────────────────────────────
  const customAmounts = useMemo(() => {
    if (!cmsHome?.data || !Array.isArray(cmsHome.data)) return [2000, 3000, 5000, 7000];
    const sectionMatch = (s) =>
      (s?.section === "Donation Box Custom Amount" || s?.section === "Custom Donation Amount Boxes") &&
      (s?.status === 1 || s?.status === true);
    let section = cmsHome.data.find(
      (s) => (s?.page === "Donation Box" || s?.page === "Donation") && sectionMatch(s)
    );
    if (!section) section = cmsHome.data.find(sectionMatch);
    if (!section) return [2000, 3000, 5000, 7000];

    const amounts = [];
    const seen = new Set();
    const tryAdd = (v) => {
      const n = Number(v);
      if (!isNaN(n) && n > 0 && !seen.has(n)) { amounts.push(n); seen.add(n); }
    };
    [section.section_title, section.title, section.sub_title, section.description].forEach(tryAdd);
    if (Array.isArray(section.relatedCMS)) {
      section.relatedCMS.forEach((item) => {
        if (item?.status === 1 || item?.status === true) {
          [item.title, item.stand_title, item.sub_title, item.description].forEach(tryAdd);
        }
      });
    }
    return amounts.length > 0 ? amounts : [2000, 3000, 5000, 7000];
  }, [cmsHome]);

  const chipAmounts = customAmounts.slice(0, 4);
  const MOST_DONATED_IDX = 2; // 3rd chip is "Most Donated"

  // Initialise chip selection to match initialAmount
  useEffect(() => {
    const idx = chipAmounts.indexOf(initialAmount);
    if (idx >= 0) setSelectedChipIdx(idx);
    else setIsOtherMode(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Amount handlers ───────────────────────────────────────────────────────
  const selectChip = (idx) => {
    setSelectedChipIdx(idx);
    setIsOtherMode(false);
    setBaseAmount(chipAmounts[idx]);
  };

  const enterOtherMode = () => {
    setSelectedChipIdx(null);
    setIsOtherMode(true);
  };

  const handleAmountInput = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, "");
    const val = Number(raw) || 0;
    setBaseAmount(val);
    const idx = chipAmounts.indexOf(val);
    if (idx >= 0) { setSelectedChipIdx(idx); setIsOtherMode(false); }
    else setSelectedChipIdx(null);
  };

  // ── Donation navigation ───────────────────────────────────────────────────
  const validateAndProceed = () => {
    const isOverseas = currency.code !== "INR";
    const v = validateDonationAmount(totalAmount, cmsHome, isOverseas, currency.symbol);
    if (!v.isValid) {
      setSnackbarMessage(v.error);
      setSnackbarOpen(true);
      return false;
    }
    return true;
  };

  const handleDonate = () => {
    if (!validateAndProceed()) return;
    if (isMonthlyMode) {
      // Monthly mode → show donor details form first
      setDetailsStep({ amount: totalAmount, frequency: "monthly", tipAmount: 0 });
      return;
    }
    // Once mode → show monthly upsell screen first. Nothing to upsell when the
    // campaign does not take monthly, so go straight to the details step; Back
    // from there already returns to this box.
    if (!monthlyAllowed) {
      proceedOnceDonation();
      return;
    }
    setShowUpsell(true);
  };

  const proceedOnceDonation = (amount = totalAmount) => {
    // amount includes the platform support tip; tipAmount lets the backend exclude it from raised.
    // isPool: "& Others" (index 0) joins the pooled program; the beneficiary-only option credits this campaign.
    setDetailsStep({ amount, frequency: "once", tipAmount: supportAmount, isPool: effectiveOnceIdx === 0 });
  };

  const handleUpsellMonthly = (amount) => {
    // Came from the donate-once upsell: no category was picked in the box, so the details step
    // shows its own "My contribution goes to" picker.
    setDetailsStep({ amount, frequency: "monthly", tipAmount: 0, fromUpsell: true });
  };

  const handlePaymentOptionClick = () => {
    if (!validateAndProceed()) return;
    proceedOnceDonation();
  };

  // ── Display helpers ───────────────────────────────────────────────────────
  const supportLabel =
    supportPct === 0 ? "0% - ₹0" : `${supportPct}% - ₹${supportAmount.toLocaleString()}`;

  // "My contribution goes to" options — fixed categories, funds are pooled.
  // Codes travel with monthly subscriptions so every recurring charge is recorded per category.
  const contributionOptions = [
    "Critical care fundraisers",
    "Rare condition fundraisers",
  ];
  const contributionCategoryCodes = ["critical_patient", "rare_diseases"];
  const selectedDonationCategory =
    contributionCategoryCodes[selectedContributionIdx] || contributionCategoryCodes[0];

  // Donate Once: two options — the beneficiary alone (this campaign only) or
  // "<beneficiary> & Others" (pooled program). Index 1 (pooled) is the default.
  const beneficiaryBase = (beneficiaryName || campaignTitle || "Beneficiary").trim();
  const onceOptions = [`${beneficiaryBase} & Others`, beneficiaryBase];

  const displayTitle = isMonthlyMode
    ? contributionOptions[selectedContributionIdx]
    : onceOptions[effectiveOnceIdx];

  const amountDisplay = isOtherMode ? String(baseAmount || "") : baseAmount.toLocaleString();

  const chipBg = (idx) => {
    if (selectedChipIdx !== idx) return DARK_CHIP;
    return idx === MOST_DONATED_IDX ? MOST_DONATED_RED : BRAND_RED;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Donor details form (monthly / quarterly subscription)
  // Back → returns to upsell (if showUpsell=true) or main box (if false)
  if (detailsStep) {
    return (
      <>
        <Box
          sx={{
            background: "#FFFFFF",
            border: "1px solid #DDE2E5",
            borderRadius: "18px",
            boxShadow: "0px 4px 4px 0px rgba(0,0,0,0.25)",
            px: "20px",
            pt: "16px",
            pb: "20px",
            width: "100%",
          }}
        >
          <SubscribeDetailsStep
            amount={detailsStep.amount}
            tipAmount={detailsStep.tipAmount || 0}
            currency={currency}
            frequency={detailsStep.frequency}
            campaignId={campaignId}
            campaignTitle={campaignTitle}
            donationCategory={selectedDonationCategory}
            contributionOptions={contributionOptions}
            contributionCategoryCodes={contributionCategoryCodes}
            showCategoryPicker={!!detailsStep.fromUpsell}
            isPoolDonation={detailsStep.isPool !== false}
            onBack={() => setDetailsStep(null)}
          />
        </Box>
      </>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Monthly upsell screen (shown after clicking Donate Once)
  if (showUpsell) {
    return (
      <>
        <Box
          sx={{
            background: "#FFFFFF",
            border: "1px solid #DDE2E5",
            borderRadius: "18px",
            boxShadow: "0px 4px 4px 0px rgba(0,0,0,0.25)",
            px: "20px",
            pt: "15px",
            pb: "20px",
            width: "100%",
          }}
        >
          <MonthlyUpsellStep
            totalAmount={totalAmount}
            currency={currency}
            onBack={() => setShowUpsell(false)}
            onMonthly={handleUpsellMonthly}
            onKeepOnce={() => proceedOnceDonation()}
          />
        </Box>
      </>
    );
  }

  // Donate Once / Donate Monthly toggle. Desktop: inside the card. Mobile:
  // rendered above the card (per the mobile design — node 1:3075).
  const toggleRow = (
    <Box sx={{ display: "flex", gap: "10px" }}>
      {/* Donate Once */}
      <Box
        component="button"
        onClick={() => setDonateMode("once")}
        sx={{
          flex: 1,
          height: "47px",
          background: donateMode === "once" ? BRAND_RED : "transparent",
          border: `1.5px solid ${MOST_DONATED_RED}`,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          cursor: "pointer",
          fontFamily: "Red Hat Display",
          fontWeight: 700,
          fontSize: isMobile ? "14px" : "18px",
          color: donateMode === "once" ? "#FFFFFF" : BRAND_RED,
          letterSpacing: isMobile ? "0.28px" : "0.36px",
          outline: "none",
          transition: "background 0.2s, color 0.2s",
        }}
      >
        <Box
          component="img"
          src={Images.donateOnceStar}
          alt=""
          sx={{
            width: isMobile ? "14px" : "19px",
            height: isMobile ? "14px" : "19px",
            filter: donateMode === "once" ? "brightness(0) invert(1)" : "none",
            transition: "filter 0.2s",
          }}
        />
        Donate Once
      </Box>

      {/* Donate Monthly */}
      <Box
        component="button"
        onClick={() => setDonateMode("monthly")}
        sx={{
          flex: 1,
          height: "47px",
          background: donateMode === "monthly" ? BRAND_RED : "transparent",
          border: `1.5px solid ${MOST_DONATED_RED}`,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          cursor: "pointer",
          fontFamily: "Red Hat Display",
          fontWeight: 700,
          fontSize: isMobile ? "14px" : "18px",
          color: donateMode === "monthly" ? "#FFFFFF" : BRAND_RED,
          letterSpacing: isMobile ? "0.28px" : "0.36px",
          outline: "none",
          transition: "background 0.2s, color 0.2s",
        }}
      >
        <Box
          component="img"
          src={Images.donateMonthlySmile}
          alt=""
          sx={{
            width: isMobile ? "14px" : "18px",
            height: isMobile ? "14px" : "18px",
            filter: donateMode === "monthly" ? "brightness(0) invert(1)" : "none",
            transition: "filter 0.2s",
          }}
        />
        Donate Monthly
      </Box>
    </Box>
  );

  // One-time-only campaigns have no Once/Monthly choice to offer, so the toggle
  // is replaced by a card header. Full-bleed: the negative margins cancel the
  // card's padding so the rule runs edge to edge, and the top radius is the
  // card's own minus its 1px border so the corners nest instead of poking out.
  const oneTimeHeader = (
    <Box
      sx={{
        mx: isMobile ? "-13px" : "-17px",
        mt: isMobile ? "-18px" : "-29px",
        px: isMobile ? "16px" : "20px",
        py: isMobile ? "12px" : "14px",
        background: "#FEF3F4",
        borderBottom: `1.5px solid ${BRAND_RED}`,
        borderTopLeftRadius: isMobile ? "11px" : "17px",
        borderTopRightRadius: isMobile ? "11px" : "17px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
      }}
    >
      <Box
        component="img"
        src={Images.donateOnceStar}
        alt=""
        sx={{ width: isMobile ? "16px" : "20px", height: isMobile ? "16px" : "20px" }}
      />
      <Typography
        sx={{
          fontFamily: "Red Hat Display",
          fontWeight: 700,
          fontSize: isMobile ? "16px" : "20px",
          color: BRAND_RED,
          letterSpacing: isMobile ? "0.28px" : "0.36px",
        }}
      >
        Donate
      </Typography>
    </Box>
  );

  return (
    <>
      {/* ══════════════════════════════════════════════════════════
          MAIN DONATION CARD
      ══════════════════════════════════════════════════════════ */}
      {/* Mobile: toggle sits above the card */}
      {isMobile && monthlyAllowed && <Box sx={{ mb: "14px" }}>{toggleRow}</Box>}
      <Box
        sx={{
          background: "#FFFFFF",
          border: isMobile ? "0.8px solid #BABABA" : "1px solid #DDE2E5",
          borderRadius: isMobile ? "12px" : "18px",
          boxShadow: "0px 4px 4px 0px rgba(0,0,0,0.25)",
          px: isMobile ? "13px" : "17px",
          pt: isMobile ? "18px" : "29px",
          pb: isMobile ? "16px" : "24px",
          display: "flex",
          flexDirection: "column",
          // Per-section spacing is set with mt on each block (Figma node
          // positions) instead of a single uniform gap, so the rhythm matches
          // the design exactly.
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Toggle — inside the card on desktop; rendered above the card on mobile */}
        {monthlyAllowed ? !isMobile && toggleRow : oneTimeHeader}

        {/* ── Contribution section ──────────────────────────────── */}
        {isMonthlyMode ? (
          /* Monthly: label + functional dropdown with contribution options */
          <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "10px" : "7px", mt: isMobile ? 0 : "22px" }}>
            <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 600, fontSize: isMobile ? "13px" : "16px", color: "#141414" }}>
              My contribution goes to:
            </Typography>
            <Box
              onClick={(e) => setContributionMenuAnchor(e.currentTarget)}
              sx={{
                background: "#FFFFFF",
                border: "0.7px solid #B9B9B8",
                borderRadius: "5px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: "12px",
                py: "8px",
                cursor: "pointer",
                gap: "6px",
              }}
            >
              <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 600, fontSize: isMobile ? "12px" : "14px", color: "#000000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                {displayTitle}
              </Typography>
              <KeyboardArrowDownIcon sx={{ fontSize: "16px", color: "#1D1D1B", flexShrink: 0 }} />
            </Box>
            <Menu
              anchorEl={contributionMenuAnchor}
              open={Boolean(contributionMenuAnchor)}
              onClose={() => setContributionMenuAnchor(null)}
              PaperProps={{ sx: { borderRadius: "10px", width: contributionMenuAnchor?.offsetWidth, maxWidth: "none" } }}
            >
              {contributionOptions.map((option, idx) => (
                <MenuItem
                  key={idx}
                  selected={selectedContributionIdx === idx}
                  onClick={() => {
                    setSelectedContributionIdx(idx);
                    setContributionMenuAnchor(null);
                  }}
                  sx={{
                    fontFamily: "Red Hat Display",
                    fontSize: { xs: "12px", sm: "14px" },
                    minHeight: { xs: "auto", sm: "48px" },
                    py: { xs: "8px", sm: "6px" },
                    fontWeight: selectedContributionIdx === idx ? 700 : 400,
                    color: selectedContributionIdx === idx ? BRAND_RED : "#1D1D1B",
                  }}
                >
                  {option}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        ) : (
          /* Donate Once: label + dropdown side by side, with "What is this?" link */
          <Box
            sx={{
              background: "#F9F9F9",
              border: "1px solid #E8E8E8",
              borderRadius: "10px",
              px: "11px",
              pt: isMobile ? "12px" : "14px",
              pb: isMobile ? "10px" : "12px",
              display: "flex",
              flexDirection: "column",
              gap: isMobile ? "8px" : "10px",
              mt: isMobile ? 0 : "22px",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "stretch" : "center", justifyContent: "space-between", gap: isMobile ? "8px" : "10px" }}>
              <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 600, fontSize: isMobile ? "13px" : "14px", color: "#666666", whiteSpace: "nowrap", flexShrink: 0 }}>
                My contribution goes to:
              </Typography>
              <Box
                onClick={monthlyAllowed ? (e) => setContributionMenuAnchor(e.currentTarget) : undefined}
                sx={{
                  // One-time-only campaigns show where the money goes but offer
                  // no choice: the pooled option does not apply to them.
                  background: monthlyAllowed ? "#FFFFFF" : "#F1F1F0",
                  border: "0.7px solid #B9B9B8",
                  borderRadius: "5px",
                  display: "flex",
                  alignItems: "center",
                  px: "10px",
                  py: "8px",
                  cursor: monthlyAllowed ? "pointer" : "default",
                  flex: isMobile ? "unset" : 1,
                  width: isMobile ? "100%" : "auto",
                  maxWidth: isMobile ? "none" : "305px",
                  gap: "6px",
                }}
              >
                <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 600, fontSize: isMobile ? "12px" : "14px", color: "#000000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                  {displayTitle}
                </Typography>
                {monthlyAllowed && (
                  <KeyboardArrowDownIcon sx={{ fontSize: "16px", color: "#1D1D1B", flexShrink: 0 }} />
                )}
              </Box>
              {/* Donate Once: beneficiary-only vs pooled ("& Others") */}
              <Menu
                anchorEl={contributionMenuAnchor}
                open={Boolean(contributionMenuAnchor)}
                onClose={() => setContributionMenuAnchor(null)}
                PaperProps={{ sx: { borderRadius: "10px", width: contributionMenuAnchor?.offsetWidth, maxWidth: "none" } }}
              >
                {onceOptions.map((option, idx) => (
                  <MenuItem
                    key={idx}
                    selected={onceOptionIdx === idx}
                    onClick={() => {
                      setOnceOptionIdx(idx);
                      setContributionMenuAnchor(null);
                    }}
                    sx={{
                      fontFamily: "Red Hat Display",
                      fontSize: { xs: "12px", sm: "14px" },
                      minHeight: { xs: "auto", sm: "48px" },
                      py: { xs: "8px", sm: "6px" },
                      fontWeight: onceOptionIdx === idx ? 700 : 400,
                      color: onceOptionIdx === idx ? BRAND_RED : "#1D1D1B",
                    }}
                  >
                    {option}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
            {/* "What is this?" explains the pooled programme — not applicable here */}
            {monthlyAllowed && (
            <Typography
              onClick={openWhatIsThis}
              sx={{
                fontFamily: "Red Hat Display",
                fontWeight: 400,
                fontSize: "12px",
                color: "#777775",
                textDecoration: "underline",
                cursor: "pointer",
                width: "fit-content",
                userSelect: "none",
                "&:hover": { color: "#4A4A49" },
              }}
            >
              What is this?
            </Typography>
            )}
          </Box>
        )}

        {/* ── Amount chips ──────────────────────────────────────── */}
        <Box sx={{ display: "flex", gap: isMobile ? "6px" : "8px", alignItems: "flex-end", mt: isMobile ? "18px" : isMonthlyMode ? "33px" : "19px" }}>
          {chipAmounts.map((amount, idx) => (
            <Box key={amount} sx={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
              {idx === MOST_DONATED_IDX ? (
                <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 500, fontSize: isMobile ? "11px" : "12px", color: "#EC1B26", textAlign: "center", lineHeight: 1, mb: isMobile ? "4px" : "6px", whiteSpace: "nowrap" }}>
                  Most Donated
                </Typography>
              ) : (
                <Box sx={{ height: "16px" }} />
              )}
              <Box
                component="button"
                onClick={() => selectChip(idx)}
                sx={{
                  width: "100%",
                  height: isMobile ? "38px" : "42px",
                  background: chipBg(idx),
                  borderRadius: isMobile ? "6px" : "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "none",
                  cursor: "pointer",
                  outline: "none",
                  transition: "background 0.15s",
                  px: "4px",
                }}
              >
                <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: isMobile ? 500 : 600, fontSize: isMobile ? "14px" : "15px", color: "#FFFFFF", letterSpacing: "-0.16px", whiteSpace: "nowrap" }}>
                  ₹{amount.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          ))}
          {/* Other chip */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
            <Box sx={{ height: "16px" }} />
            <Box
              component="button"
              onClick={enterOtherMode}
              sx={{
                width: "100%",
                height: isMobile ? "38px" : "42px",
                background: isOtherMode ? BRAND_RED : DARK_CHIP,
                borderRadius: isMobile ? "6px" : "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "none",
                cursor: "pointer",
                outline: "none",
                transition: "background 0.15s",
              }}
            >
              <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: isMobile ? 500 : 600, fontSize: isMobile ? "14px" : "15px", color: "#FFFFFF", letterSpacing: "-0.16px" }}>
                Other
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ── "Support us by" — Donate Once only ───────────────── */}
        {!isMonthlyMode && (
          <Box
            sx={{
              background: "#F1F1F1",
              borderRadius: "10px",
              p: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              mt: isMobile ? "18px" : "29px",
            }}
          >
            <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 600, fontSize: isMobile ? "14px" : "16px", color: "#141414", whiteSpace: "nowrap", flexShrink: 0 }}>
              Support us by :
            </Typography>
            <Box
              onClick={(e) => setSupportMenuAnchor(e.currentTarget)}
              sx={{
                background: "#FFFFFF",
                border: "1px solid #B9B9B8",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: "12px",
                height: "44px",
                minWidth: "148px",
                cursor: "pointer",
                gap: "8px",
              }}
            >
              <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 600, fontSize: "14px", color: "#1D1D1B", whiteSpace: "nowrap" }}>
                {supportLabel}
              </Typography>
              <KeyboardArrowDownIcon sx={{ fontSize: "22px", color: "#1D1D1B" }} />
            </Box>
            <Menu
              anchorEl={supportMenuAnchor}
              open={Boolean(supportMenuAnchor)}
              onClose={() => setSupportMenuAnchor(null)}
              // Short and scrollable: ~3.5 rows show, so the cut-off row hints there's
              // more and the donor scrolls down to reach 0%.
              PaperProps={{ sx: { borderRadius: "10px", minWidth: "156px", maxHeight: "140px", overflowY: "auto" } }}
            >
              {[12, 15, 20, 25, 0].map((pct) => (
                <MenuItem
                  key={pct}
                  onClick={() => { setSupportPct(pct); setSupportMenuAnchor(null); }}
                  sx={{ fontFamily: "Red Hat Display", fontSize: "14px" }}
                >
                  {pct}%{pct > 0 ? ` - ₹${Math.round(baseAmount * pct / 100).toLocaleString()}` : ""}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        )}

        {/* ── Amount input ──────────────────────────────────────── */}
        <Box
          sx={{
            background: "#F5F8ED",
            border: "1px solid #BDD188",
            borderRadius: "9px",
            px: "16px",
            height: isMobile ? "48px" : "54px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            boxSizing: "border-box",
            mt: isMobile ? "18px" : isMonthlyMode ? "39px" : "20px",
            transition: "background 0.15s, border-color 0.15s",
            // While the user is editing the amount, the field turns white with a
            // grey border.
            "&:focus-within": {
              background: "#FFFFFF",
              borderColor: "#B9B9B8",
            },
          }}
        >
          <Box
            onClick={(e) => setCurrencyMenuAnchor(e.currentTarget)}
            sx={{ display: "flex", alignItems: "center", gap: "3px", cursor: "pointer", flexShrink: 0 }}
          >
            <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 700, fontSize: isMobile ? "14px" : "18px", color: "#4A4A49" }}>
              {currency.symbol}
            </Typography>
            <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 700, fontSize: isMobile ? "14px" : "18px", color: "#4A4A49" }}>
              {currency.code}
            </Typography>
            <KeyboardArrowDownIcon sx={{ fontSize: isMobile ? "20px" : "22px", color: "#4A4A49" }} />
          </Box>

          <Box sx={{ width: "1px", height: "22px", background: "#B9B9B8", flexShrink: 0 }} />

          <Box
            component="input"
            type="text"
            inputMode="numeric"
            value={amountDisplay}
            onChange={handleAmountInput}
            onFocus={enterOtherMode}
            sx={{
              fontFamily: "Red Hat Display",
              fontWeight: 700,
              fontSize: isMobile ? "14px" : "18px",
              color: "#4A4A49",
              background: "transparent",
              border: "none",
              outline: "none",
              flex: 1,
              minWidth: 0,
            }}
          />

          {/* "Monthly" badge — only in monthly mode */}
          {isMonthlyMode && (
            <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 700, fontSize: isMobile ? "14px" : "18px", color: "#A9C74E", flexShrink: 0 }}>
              Monthly
            </Typography>
          )}
        </Box>

        <Menu
          anchorEl={currencyMenuAnchor}
          open={Boolean(currencyMenuAnchor)}
          onClose={() => setCurrencyMenuAnchor(null)}
          PaperProps={{ sx: { borderRadius: "10px", minWidth: "130px" } }}
        >
          {currencyList.map((cur) => (
            <MenuItem
              key={cur.code}
              onClick={() => { setCurrency(cur); setCurrencyMenuAnchor(null); }}
              sx={{ fontFamily: "Red Hat Display", fontSize: "14px" }}
            >
              {cur.symbol} {cur.code}
            </MenuItem>
          ))}
        </Menu>

        {/* ── Donate / Continue button ──────────────────────────── */}
        <Box
          component="button"
          onClick={handleDonate}
          sx={{
            background: BRAND_RED,
            borderRadius: isMobile ? "8px" : "11px",
            width: "100%",
            height: isMobile ? "46px" : "58px",
            mt: isMobile ? "20px" : "33px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            cursor: "pointer",
            boxShadow: "0px 4px 0px #141414",
            outline: "none",
            transition: "opacity 0.15s",
            "&:hover": { opacity: 0.92 },
            "&:active": { transform: "translateY(2px)", boxShadow: "0px 2px 0px #141414" },
          }}
        >
          <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 700, fontSize: isMobile ? "14px" : "16px", color: "#FFFFFF", userSelect: "none" }}>
            {isMonthlyMode
              ? "Continue Donation"
              : `Donate ${currency.symbol}${totalAmount.toLocaleString()}`}
          </Typography>
        </Box>

        {/* ── Donate Via ────────────────────────────────────────── */}
        <Box sx={{ mt: isMobile ? "18px" : "21px" }}>
          <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 700, fontSize: isMobile ? "14px" : "16px", color: "#000000", mb: "7px" }}>
            Donate Via
          </Typography>
          <Box
            sx={{
              background: DARK_CHIP,
              borderRadius: isMobile ? "12px" : "15px",
              px: isMobile ? "6px" : "12px",
              py: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-around",
            }}
          >
            {paymentOptions.map((option) => (
              <Box
                key={option.name}
                onClick={handlePaymentOptionClick}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  width: "56px",
                  "&:hover": { opacity: 0.8 },
                }}
              >
                <Box component="img" src={option.src} alt={option.name} sx={{ width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover" }} />
                <Typography sx={{ fontFamily: "Red Hat Display", fontWeight: 400, fontSize: "12px", color: "#FFFFFF", textAlign: "center", lineHeight: "16.8px" }}>
                  {option.name}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ══════════════════════════════════════════════════════════
          HOW TO DONATE V2 (separate card below, uses existing CMS)
      ══════════════════════════════════════════════════════════ */}
      <Box sx={{ mt: "16px" }}>
        {showHowToDonate && <HowToDonateV2 />}
      </Box>

      {/* ── Popovers & overlays ───────────────────────────────── */}
      <WhatIsThisPopover
        anchorEl={whatIsThisAnchor}
        onClose={() => setWhatIsThisAnchor(null)}
        beneficiaries={beneficiaries}
      />

      <CurrencySelectorPopup
        open={showCurrencyPopup}
        onClose={() => setShowCurrencyPopup(false)}
        selectedCurrency={currency}
        onCurrencySelect={(cur) => { setCurrency(cur); setShowCurrencyPopup(false); }}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="error" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DonationBoxV2;
